import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  SettlementWallet,
  FlushSettlementWalletResponse,
} from '../../models/settlement-wallet.model';

import { SettlementWalletService } from '../../services/settlement-wallet.service';
import { AuthService } from '../../services/auth.service';

const FLUSH_CURRENCIES = ['USDC', 'EURC', 'USDT'] as const;
type FlushCurrency = (typeof FLUSH_CURRENCIES)[number];

@Component({
  selector: 'app-settlement-wallets',
  standalone: false,
  templateUrl: './settlement-wallets.component.html',
  styleUrl: './settlement-wallets.component.css',
})
export class SettlementWalletsComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() userUuid!: string;
  @Input() privyWalletAddress: string | null = null;
  @Output() flushed = new EventEmitter<FlushSettlementWalletResponse>();

  wallets: SettlementWallet[] = [];
  loading = false;
  error: string | null = null;
  copiedAddress: string | null = null;

  // Flush modal state
  isFlushModalOpen = false;
  selectedWallet: SettlementWallet | null = null;
  selectedCurrency: FlushCurrency = 'USDC';
  flushAmount: number = 0;
  transactionUuid = '';
  isFlushing = false;
  flushError: string | null = null;
  flushSuccess: FlushSettlementWalletResponse | null = null;

  // Timer for cooldown updates
  private cooldownInterval: any = null;
  private readonly COOLDOWN_DURATION_MS = 5 * 60 * 1000; // 5 minutes

  constructor(
    private settlementWalletService: SettlementWalletService,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    if (this.userUuid) {
      this.fetchWallets();
    }
    // Periodic tick to refresh cooldown countdowns in the UI
    this.cooldownInterval = setInterval(() => {}, 1000);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userUuid'] && !changes['userUuid'].firstChange) {
      if (this.userUuid) {
        this.fetchWallets();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
    }
  }

  get feesCollected(): number {
    return this.flushSuccess?.fees_collected ?? 0;
  }

  get flushedAmount(): number {
    return this.flushSuccess?.flushed_amount ?? this.flushSuccess?.amount ?? 0;
  }

  get canFlush(): boolean {
    return (
      this.authService.isComplianceAdmin() ||
      this.authService.isWalletsManager() ||
      this.authService.isCustomerSupportMember()
    );
  }

  getUsdc(wallet: SettlementWallet | null | undefined): number {
    return wallet?.balance?.USDC ?? 0;
  }

  getEurc(wallet: SettlementWallet | null | undefined): number {
    return wallet?.balance?.EURC ?? 0;
  }

  getUsdt(wallet: SettlementWallet | null | undefined): number {
    return wallet?.balance?.USDT ?? 0;
  }

  getFlushableCurrencies(
    wallet?: SettlementWallet | null,
  ): FlushCurrency[] {
    const b = wallet?.balance;
    if (!b) return [];
    return FLUSH_CURRENCIES.filter((curr) => (b[curr] ?? 0) >= 0.01);
  }

  // Default to the wallet's declared currency when it holds funds, otherwise the
  // first currency that does.
  getDefaultFlushCurrency(wallet?: SettlementWallet | null): FlushCurrency {
    const flushable = this.getFlushableCurrencies(wallet);
    const declared = wallet?.currency?.toUpperCase() as FlushCurrency;
    if (flushable.includes(declared)) return declared;
    return flushable[0] ?? 'USDC';
  }

  getBalance(
    wallet: SettlementWallet | null | undefined,
    currency: FlushCurrency,
  ): number {
    return wallet?.balance?.[currency] ?? 0;
  }

  get flushCurrency(): FlushCurrency {
    return this.selectedCurrency;
  }

  get availableFlushBalance(): number {
    return this.getBalance(this.selectedWallet, this.selectedCurrency);
  }

  get selectedWalletFlushableCurrencies(): FlushCurrency[] {
    return this.getFlushableCurrencies(this.selectedWallet);
  }

  hasFlushableBalance(wallet: SettlementWallet): boolean {
    return this.getFlushableCurrencies(wallet).length > 0;
  }

  hasAnyBalance(wallet: SettlementWallet): boolean {
    const b = wallet?.balance;
    if (!b) return false;
    return (b.USDC ?? 0) > 0 || (b.EURC ?? 0) > 0 || (b.USDT ?? 0) > 0;
  }

  fetchWallets(): void {
    if (!this.userUuid) return;
    this.loading = true;
    this.error = null;

    this.settlementWalletService.getSettlementWallets(this.userUuid).subscribe({
      next: (data) => {
        this.wallets = data || [];
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading settlement wallets:', err);
        this.error = 'Failed to load settlement wallets. Please try again.';
        this.loading = false;
      },
    });
  }

  copyAddress(address: string): void {
    navigator.clipboard.writeText(address).then(() => {
      this.copiedAddress = address;
      setTimeout(() => {
        if (this.copiedAddress === address) {
          this.copiedAddress = null;
        }
      }, 2000);
    });
  }

  private getCooldownKey(walletUuid: string): string {
    return `flushCooldownUntil_${walletUuid}`;
  }

  isCoolingDown(walletUuid: string): boolean {
    const until = localStorage.getItem(this.getCooldownKey(walletUuid));
    if (!until) return false;
    return Date.now() < Number(until);
  }

  getCooldownRemaining(walletUuid: string): string {
    const until = localStorage.getItem(this.getCooldownKey(walletUuid));
    if (!until) return '';
    const diffSeconds = Math.max(0, Math.ceil((Number(until) - Date.now()) / 1000));
    const mins = Math.floor(diffSeconds / 60);
    const secs = diffSeconds % 60;
    return `${mins}m ${secs < 10 ? '0' : ''}${secs}s`;
  }

  setCooldown(walletUuid: string): void {
    localStorage.setItem(
      this.getCooldownKey(walletUuid),
      String(Date.now() + this.COOLDOWN_DURATION_MS),
    );
  }

  openFlushModal(wallet: SettlementWallet): void {
    this.selectedWallet = wallet;
    this.selectedCurrency = this.getDefaultFlushCurrency(wallet);
    this.flushAmount = this.availableFlushBalance;
    this.transactionUuid = '';
    this.flushError = null;
    this.flushSuccess = null;
    this.isFlushModalOpen = true;
  }

  closeFlushModal(): void {
    if (this.isFlushing) return;
    this.isFlushModalOpen = false;
    this.selectedWallet = null;
    this.flushError = null;
    this.flushSuccess = null;
  }

  selectFlushCurrency(currency: FlushCurrency): void {
    if (this.isFlushing) return;
    this.selectedCurrency = currency;
    this.flushAmount = this.availableFlushBalance;
    this.flushError = null;
  }

  setMaxAmount(): void {
    if (this.selectedWallet) {
      this.flushAmount = this.availableFlushBalance;
    }
  }

  get isTransactionUuidValid(): boolean {
    const value = this.transactionUuid.trim();
    return (
      !value ||
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        value,
      )
    );
  }

  get isAmountValid(): boolean {
    if (!this.selectedWallet) return false;
    return (
      this.flushAmount >= 0.01 &&
      this.flushAmount <= this.availableFlushBalance
    );
  }

  submitFlush(): void {
    if (
      !this.selectedWallet ||
      !this.isAmountValid ||
      !this.isTransactionUuidValid ||
      !this.canFlush ||
      this.isFlushing ||
      this.isCoolingDown(this.selectedWallet.uuid)
    ) {
      return;
    }

    this.isFlushing = true;
    this.flushError = null;
    this.flushSuccess = null;

    const walletUuid = this.selectedWallet.uuid;

    this.settlementWalletService
      .flushSettlementWallet(
        this.userUuid,
        walletUuid,
        this.flushAmount,
        this.selectedCurrency,
        this.transactionUuid.trim() || null,
      )
      .subscribe({
        next: (res) => {
          this.isFlushing = false;
          this.flushSuccess = res;
          this.setCooldown(walletUuid);
          this.fetchWallets();
          this.flushed.emit(res);
        },
        error: (err) => {
          this.isFlushing = false;
          console.error('Error flushing settlement wallet:', err);
          if (err.error?.message) {
            this.flushError = err.error.message;
          } else if (err.error?.amount && Array.isArray(err.error.amount)) {
            this.flushError = err.error.amount.join(', ');
          } else {
            this.flushError =
              'An unexpected error occurred while flushing funds. Please verify balances and try again.';
          }
        },
      });
  }
}
